import { useScrollReveal } from "@/hooks/useScrollReveal";

interface BodyTypeItem {
  name: string;
  subtext: string;
  count: string;
  image: string;
  href: string;
}

const bodyTypes: BodyTypeItem[] = [
  {
    name: "SUV",
    subtext: "Crossovers & SUVs",
    count: "37 Available",
    image:
      "https://public.readdy.ai/ai/img_res/96ddadb64a86ed8f381c42f20a37647b.jpg",
    href: "https://www.uagmn.com/inventory/?body_type=SUV",
  },
  {
    name: "Sedan",
    subtext: "Cars & Sedans",
    count: "19 Available",
    image:
      "https://readdy.ai/api/search-image?query=elegant%20luxury%20sedan%20parked%20on%20wet%20city%20street%20at%20night%20reflections%20bokeh%20golden%20amber%20lights%20urban%20environment%20cinematic%20premium%20automotive%20photography%20dark%20sophisticated%20moody%20atmosphere%20deep%20shadows&width=1200&height=800&seq=uag-bt-sedan-v1&orientation=landscape",
    href: "https://www.uagmn.com/inventory/?body_type=SEDAN",
  },
  {
    name: "Truck",
    subtext: "Pickup Trucks",
    count: "1 Available",
    image:
      "https://readdy.ai/api/search-image?query=powerful%20pickup%20truck%20parked%20on%20rugged%20terrain%20red%20rock%20desert%20dramatic%20storm%20clouds%20warm%20orange%20earth%20tones%20masculine%20capability%20cinematic%20automotive%20photography%20wide%20angle%20adventure%20premium%20quality%20dark&width=1200&height=800&seq=uag-bt-truck-v1&orientation=landscape",
    href: "https://www.uagmn.com/inventory/?body_type=PICKUP",
  },
  {
    name: "Coupe",
    subtext: "Sports & Coupes",
    count: "1 Available",
    image:
      "https://readdy.ai/api/search-image?query=sleek%20sporty%20coupe%20on%20coastal%20highway%20dramatic%20sunset%20orange%20sky%20ocean%20view%20cliffs%20premium%20automotive%20lifestyle%20photography%20wide%20angle%20warm%20tones%20elegant%20prestige%20cinematic%20dark%20foreground%20depth&width=1200&height=800&seq=uag-bt-coupe-v1&orientation=landscape",
    href: "https://www.uagmn.com/inventory/?body_type=COUPE",
  },
];

interface CardProps {
  item: BodyTypeItem;
  isVisible: boolean;
  delay: number;
}

function BodyTypeCard({ item, isVisible, delay }: CardProps) {
  return (
    <a
      href={item.href}
      rel="noopener noreferrer"
      className={`group relative block overflow-hidden rounded-xl cursor-pointer transition-all duration-700 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
      style={{ transitionDelay: `${delay}ms`, height: "380px" }}
      aria-label={`Browse ${item.name} inventory — ${item.count}`}
    >
      {/* Background image */}
      <img
        src={item.image}
        alt={`${item.name} vehicles at United Auto Group Farmington MN`}
        className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.05]"
      />

      {/* Strong gradient from bottom — ensures text always readable */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />
      {/* Hover overlay */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-400" />

      {/* Count badge */}
      <div className="absolute top-5 left-5">
        <span className="inline-flex items-center gap-1.5 bg-black/60 backdrop-blur-sm border border-white/20 text-white text-xs font-semibold px-3 py-1.5 rounded-full tracking-wide">
          <span className="w-1.5 h-1.5 rounded-full bg-gold-DEFAULT inline-block" />
          {item.count}
        </span>
      </div>

      {/* Bottom content */}
      <div className="absolute bottom-0 left-0 right-0 p-7">
        <p className="text-gold-DEFAULT text-xs font-semibold tracking-[0.2em] uppercase font-sans mb-2">
          {item.subtext}
        </p>
        <h3
          className="font-serif text-white leading-none mb-4 group-hover:text-gold-light transition-colors duration-300"
          style={{ fontSize: "clamp(2rem, 3.5vw, 2.6rem)" }}
        >
          {item.name}
        </h3>
        <div className="inline-flex items-center gap-2 bg-gold-DEFAULT/15 border border-gold-DEFAULT/40 text-white text-xs font-semibold px-4 py-2 rounded-full group-hover:bg-gold-DEFAULT group-hover:text-charcoal transition-all duration-300 whitespace-nowrap">
          Explore Inventory
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-arrow-right-line text-xs group-hover:translate-x-0.5 transition-transform duration-300" />
          </div>
        </div>
      </div>

      {/* Gold left accent */}
      <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gold-DEFAULT scale-y-0 group-hover:scale-y-100 transition-transform duration-500 origin-bottom" />
    </a>
  );
}

export default function BodyTypeGrid() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal();
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal();
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollReveal();

  return (
    <section className="bg-[#f7f5f2] py-24 md:py-32" aria-labelledby="body-types-heading">
      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* Section Header */}
        <div
          ref={headerRef}
          className={`flex flex-col md:flex-row md:items-end justify-between mb-14 gap-5 transition-all duration-700 ${
            headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div>
            <div className="inline-flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-gold-DEFAULT" />
              <span className="text-gold-DEFAULT text-xs font-bold tracking-[0.25em] uppercase font-sans">
                SHOP BY BODY TYPE
              </span>
            </div>
            <h2
              id="body-types-heading"
              className="font-serif text-[#1a1612] leading-none"
              style={{ fontSize: "clamp(2.2rem, 4vw, 3.2rem)", lineHeight: 1.05 }}
            >
              Find Your Vehicle<br />
              <em className="not-italic text-gold-DEFAULT">By Body Style</em>
            </h2>
          </div>
          <p className="text-[#5a4f47] font-sans text-sm leading-relaxed max-w-xs md:text-right">
            Browse our curated pre-owned inventory by the body style that fits your life. Updated regularly with hand-selected vehicles.
          </p>
        </div>

        {/* 4-card grid — 2 columns desktop, 1 mobile */}
        <div
          ref={gridRef}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6"
        >
          {bodyTypes.map((item, i) => (
            <BodyTypeCard
              key={item.name}
              item={item}
              isVisible={gridVisible}
              delay={i * 100}
            />
          ))}
        </div>

        {/* CTA Bar */}
        <div
          ref={ctaRef}
          className={`flex flex-col sm:flex-row items-center justify-between gap-5 bg-[#1a1612] rounded-xl px-8 py-7 transition-all duration-700 ${
            ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
          style={{ transitionDelay: "300ms" }}
        >
          <div>
            <p className="font-serif text-white text-xl md:text-2xl leading-tight mb-1.5">
              Not sure which body type fits you?
            </p>
            <p className="text-white/80 text-sm font-sans">
              Call{" "}
              <span className="text-gold-DEFAULT font-semibold">(952) 769-3208</span>
              {" "}— our team helps you choose the right vehicle.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <a
              href="https://www.uagmn.com/inventory/"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-gold-DEFAULT text-[#1a1612] font-bold text-sm px-8 py-4 rounded-full hover:bg-gold-light transition-colors duration-200 whitespace-nowrap cursor-pointer tracking-wide"
            >
              View All Vehicles
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </div>
            </a>
            <a
              href="tel:9527693208"
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-white/10 hover:border-white/60 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              aria-label="Call (952) 769-3208"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line text-gold-DEFAULT text-sm" />
              </div>
              (952) 769-3208
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}